import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Building2, Search, PenSquare, Plus } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const TenancyContractList = () => {
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingID, setSelectedBuildingID] = useState('');
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingID) {
      fetchContracts(selectedBuildingID);
    } else {
      setContracts([]);
    }
  }, [selectedBuildingID]);

  const fetchBuildings = async () => {
    setLoadingBuildings(true);
    const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    const companyCode = localStorage.getItem("companyCode") || "-";
    try {
      const res = await axios.get(`${apiPaths[0]?.API_CONFIG}/api/CRMTaskMainListFilter/${companyCode}/BUILDING/-/-/-/-/-/1900-01-01/1900-01-01/-/-/-`);
      const data = Array.isArray(res.data) ? res.data : [];
      setBuildings(data);
      if (data.length > 0 && !selectedBuildingID) {
        setSelectedBuildingID(data[0].BuildingID);
      }
    } catch (err) {
      console.error('Failed to fetch buildings:', err);
    } finally {
      setLoadingBuildings(false);
    }
  };

  const fetchContracts = async (buildingId) => {
    setLoadingContracts(true);
    const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    const companyCode = localStorage.getItem("companyCode") || "-";
    try {
      const res = await axios.get(`${apiPaths[0]?.API_CONFIG}/api/CRMTaskMainListFilter/${companyCode}/CONTRACTLIST/-/-/-/${buildingId}/MAIN/1900-01-01/1900-01-01/-/-/-`);
      setContracts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch contracts:', err);
      setContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  const filteredContracts = contracts.filter((c) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      (c.contractNo || '').toLowerCase().includes(q) ||
      (c.Tenant || '').toLowerCase().includes(q) ||
      (c.Account || '').toLowerCase().includes(q) ||
      (c.Subject || '').toLowerCase().includes(q) ||
      (c.Unit_ID || '').toLowerCase().includes(q)
    );
  });

  const formatDate = (val) => {
    if (!val) return '-';
    return val.split('T')[0];
  };

  const handleEdit = (contract) => {
    navigate('/tenancyContractRegister', {
      state: {
        contractNo: contract.contractNo,
        buildingId: contract.Building || selectedBuildingID,
      }
    });
  };

  const handleNew = () => {
    navigate('/tenancyContractRegister');
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-6 text-slate-800 font-sans">

      {/* Header */}
      <div className="mx-auto bg-white rounded-xl border border-slate-200 shadow-xs p-4 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 mt-0.5">Tenancy Contract List</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Building
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                  <Building2 className="w-3.5 h-3.5" />
                </span>
                <select
                  value={selectedBuildingID}
                  onChange={(e) => setSelectedBuildingID(e.target.value)}
                  className="pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all cursor-pointer min-w-[220px]"
                >
                  <option value="">Select Building</option>
                  {buildings.map((b) => (
                    <option key={b.BuildingID} value={b.BuildingID}>
                      {b.BuildingID} ({b.BuildingName})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search contracts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none w-[220px]"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold text-transparent uppercase tracking-wider">
                Action
              </label>
              <button
                type="button"
                onClick={handleNew}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-all shadow-xs"
              >
                <Plus className="w-4 h-4" />
                New Contract
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contracts Table */}
      <div className="mx-auto">
        <Card>
          {loadingContracts ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider">
                    <th className="p-3.5">Contract No</th>
                    <th className="p-3.5">Date</th>
                    <th className="p-3.5">Tenant</th>
                    <th className="p-3.5">Unit</th>
                    <th className="p-3.5">Type</th>
                    <th className="p-3.5">Period From</th>
                    <th className="p-3.5">Period To</th>
                    <th className="p-3.5 text-right">Rent</th>
                    <th className="p-3.5 text-right">Rent VAT</th>
                    <th className="p-3.5 text-right">Deposit</th>
                    <th className="p-3.5 text-right">Commission</th>
                    <th className="p-3.5 text-right">Comm. VAT</th>
                    <th className="p-3.5">Status</th>
                    <th className="p-3.5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-mono">
                  {filteredContracts.length > 0 ? (
                    filteredContracts.map((c, index) => (
                      <tr
                        key={c.contractNo || index}
                        className="hover:bg-slate-50/60 transition-colors"
                      >
                        <td className="p-3.5 text-blue-600 font-sans font-medium">{c.contractNo}</td>
                        <td className="p-3.5 font-sans">{formatDate(c.ContDate)}</td>
                        <td className="p-3.5 font-sans text-slate-600 max-w-[200px] truncate" title={c.Tenant}>
                          {c.Tenant?.trim() || '-'}
                        </td>
                        <td className="p-3.5 font-sans">{c.Unit_ID || '-'}</td>
                        <td className="p-3.5">
                          <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-sans text-[11px] font-medium">
                            {c.Type || '-'}
                          </span>
                        </td>
                        <td className="p-3.5 font-sans">{formatDate(c.PeriodFrom)}</td>
                        <td className="p-3.5 font-sans">{formatDate(c.PeriodTo)}</td>
                        <td className="p-3.5 text-right font-semibold text-slate-900">
                          {Number(c.Rent || 0).toLocaleString()}
                        </td>
                        <td className="p-3.5 text-right text-slate-600">
                          {Number(c.RentVat || 0).toLocaleString()}
                        </td>
                        <td className="p-3.5 text-right text-slate-600">
                          {Number(c.Deposit || 0).toLocaleString()}
                        </td>
                        <td className="p-3.5 text-right text-slate-600">
                          {Number(c.Commission || 0).toLocaleString()}
                        </td>
                        <td className="p-3.5 text-right text-slate-600">
                          {Number(c.CommissionVat || 0).toLocaleString()}
                        </td>
                        <td className="p-3.5">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            c.STATUS1 === 'APPROVED'
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-amber-100 text-amber-700 border border-amber-200'
                          }`}>
                            {c.STATUS1 || '-'}
                          </span>
                        </td>
                        <td className="p-3.5 text-center">
                          <button
                            type="button"
                            onClick={() => handleEdit(c)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Edit Contract"
                          >
                            <PenSquare className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={14} className="text-center py-12 text-sm text-slate-400 font-medium font-sans">
                        {selectedBuildingID
                          ? 'No contracts found for this building.'
                          : 'Select a building to view contracts.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default TenancyContractList;
